import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { SelectChangeEvent, SelectProps } from "@mui/material/Select";
import { FormControl, Grid, Select as MuiSelect } from "@mui/material";
import { HelperText, InputLabel } from "common/styles/muiStyled";
import { FREYA_ORIENTATION } from "../../../../../common/enums";
import colors from "../../../../../common/styles/colors";
import { FREYA_ORIENTATION_KEY } from "../../../../../common/constants";
import { makeStyles } from "@mui/styles";
import { ComponentWithChildren } from "../../../../../common/types";

interface Props {
  name: string;
  value: string;
  onChange: (event: SelectChangeEvent) => void;
  helperText?: React.ReactNode;
  error?: boolean;
  disabled?: boolean;
  withSelectOption?: boolean;
  label?: string;
  renderIcon?: () => JSX.Element;
  native?: boolean;
  optional?: boolean;
  sx?: SelectProps["sx"] | false;
  customOnClick?: () => void;
}

const useStyles = makeStyles({
  select: {
    height: "32.13px !important",
    fontSize: "14px !important",
    borderColor: colors.black,
    "& fieldset": {
      borderColor: colors.lightGrey4
    }
  }
});

const Select: ComponentWithChildren<Props> = ({
  renderIcon,
  name,
  value,
  onChange,
  children,
  helperText,
  error,
  disabled,
  withSelectOption = true,
  label,
  native = true,
  optional = false,
  sx,
  customOnClick
}) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const ref = useRef<HTMLDivElement>();
  const [selectProps, setSelectProps] = useState<any>();

  const handleDropdownPosition = () => {
    const orientation = localStorage.getItem(FREYA_ORIENTATION_KEY) || FREYA_ORIENTATION.NORMAL;
    if (orientation === FREYA_ORIENTATION.ROTATED_180) {
      const clientRects = ref.current?.getClientRects()![0]!;
      setSelectProps({
        top: `${clientRects && clientRects.top - 5}px !important`,
        transform: "rotate(180deg) !important",
        transformOrigin: `50% 0px !important`
      });
      return;
    }
    setSelectProps(undefined);
  };

  return (
    <Grid item xs={12} sm={6} sx={{ pt: 3 }} onMouseOver={handleDropdownPosition}>
      <FormControl fullWidth sx={{ minWidth: 200 }}>
        <InputLabel sx={{ display: "flex", alignItems: "center" }}>
          <span>
            {label || t(`common:badgeForm.${name}`)}
            {optional && <span data-testid="optional-badge-field-title">{` (${t("common:badgeForm.optional")})`}</span>}
          </span>
          {renderIcon && <span style={{ marginLeft: "auto", marginBottom: -3 }}>{renderIcon()}</span>}
        </InputLabel>
        <MuiSelect
          className={classes.select}
          MenuProps={{
            PaperProps: {
              sx: selectProps
            }
          }}
          native={native}
          size={"small"}
          ref={ref}
          name={name}
          value={value}
          onChange={onChange}
          error={error}
          data-testid={`badge-form-select-${name}`}
          disabled={disabled}
          sx={sx || {}}
          onMouseDown={customOnClick ? e => e.preventDefault() : undefined}
          onClick={customOnClick}
        >
          <option key={0} value="" hidden style={{ display: "none" }}>
            {withSelectOption ? `${t("common:common.selectLabels.select")}...` : ""}
          </option>
          {children}
        </MuiSelect>
        {helperText && (
          <HelperText sx={{ color: colors.redColor1 }} id={`badge-form-${name}`}>
            {helperText}
          </HelperText>
        )}
      </FormControl>
    </Grid>
  );
};

export default Select;
